import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { FeatherClock, FeatherCircle, FeatherClipboard, FeatherPin, FeatherTarget, FeatherChevronDown, FeatherChevronUp, FeatherCheckCircle, FeatherAlertCircle } from '../ui/icons';
import { taskApi, workPlanApi } from '../../services/api';
import './Dashboard.css';
import { supabase } from '../../config/supabase';
import { TextArea } from '../ui/TextArea';
import { TextField } from '../ui/TextField';
import { ProjectBadge } from '../ui/project-badge';
import { FaPaperclip, FaChevronDown, FaChevronUp, FaExclamationTriangle } from 'react-icons/fa';
import api from '../../services/api';
import { IconButton } from "../ui/IconButton";
import NoteReplies from "../shared/NoteReplies";
import TaskTypeIcon from '../shared/TaskTypeIcon';
import TaskDueDate from '../shared/TaskDueDate';
import { FeatureTag } from '../ui/feature-tag';
import FeatureTagDetailModal from '../FeatureTags/FeatureTagDetailModal';
import { isManager, isMember } from '../../utils/roleUtils';
import { userApi } from '../../services/api';
import OverdueCards from './components/OverdueCards';
import AccessibleProjectsShortcut from './components/AccessibleProjectsShortcut';
import { dailyReviewService } from '../../services/dailyReviewService';
import VersionCheckBanner from '../shared/VersionCheckBanner';
import TaskSummaryModal from '../../pages/components/TaskSummaryModal';
import { Info, Paperclip as PaperclipIcon } from 'react-feather';

const TASK_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  RELEASED: 'released'
};

// Default avatar as a data URL - light gray circle with user initials
const getDefaultAvatar = (name) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = 200;
  canvas.height = 200;

  // Draw circle
  context.fillStyle = '#E0E0E0';
  context.beginPath();
  context.arc(100, 100, 100, 0, Math.PI * 2, true);
  context.fill();

  // Draw initials
  const initials = name
    ? name
        .split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '?';

  context.fillStyle = '#757575';
  context.font = 'bold 80px Arial';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(initials, 100, 100);

  return canvas.toDataURL('image/png');
};



const WorkDayCompleteCard = ({ closedAt }) => {
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="work-day-complete-card">
      <div className="card-header">
        <div className="icon-circle">
          <FeatherClipboard className="check-icon" />
        </div>
        <h3>Work Day Complete</h3>
      </div>
      <div className="card-content">
        <div className="info-row">
          <FeatherCircle className="info-icon" />
          <div className="info-text">
            <span className="label">Closed on</span>
            <span className="value">{formatDate(closedAt)}</span>
          </div>
        </div>
        <div className="info-row">
          <FeatherClock className="info-icon" />
          <div className="info-text">
            <span className="label">Time</span>
            <span className="value">{formatTime(closedAt)}</span>
          </div>
        </div>
        <div className="message">
          <p>You've successfully completed your work plan for today. You can create a new work plan tomorrow.</p>
        </div>
      </div>
    </div>
  );
};

const PinnedNotesSection = ({ notes, navigate, user }) => {
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(true);
  
  if (!notes || notes.length === 0) return null;
  
  const toggleExpanded = (noteId) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedNotes(newExpanded);
  };
  
  return (
    <div className="pinned-notes-section">
      <div 
        className="pinned-notes-section-header"
        onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
      >
        <div className="section-header-content">
          <h2 className="text-lg font-semibold text-default-font">📌 Pinned Notes</h2>
          {isSectionCollapsed && (
            <div className="section-summary">
              <span className="notes-count">{notes.length} {notes.length === 1 ? 'note' : 'notes'}</span>
              {notes.length > 0 && (
                <span className="notes-preview">
                  • {notes[0].title || notes[0].content.substring(0, 40) + '...'}
                </span>
              )}
            </div>
          )}
        </div>
        <button 
          className="collapse-toggle-btn"
          type="button"
        >
          {isSectionCollapsed ? <FaChevronDown size={16} /> : <FaChevronUp size={16} />}
        </button>
      </div>
      <div className={`pinned-notes-grid ${isSectionCollapsed ? 'collapsed' : ''}`}>
        {notes.map(note => {
          const isExpanded = expandedNotes.has(note.id);
          const shouldTruncate = note.content.length > 300;
          const displayContent = shouldTruncate && !isExpanded 
            ? note.content.substring(0, 300) 
            : note.content;
          
          return (
            <div key={note.id} className="pinned-note-card">
              <div className="pinned-note-header">
                <div className="pinned-note-meta">
                  <span className="pinned-indicator">
                    <FeatherPin size={12} />
                  </span>
                  <span className="note-date">
                    {new Date(note.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                <Button 
                  variant="neutral-tertiary" 
                  size="small"
                  onClick={() => navigate('/scribble')}
                >
                  View All
                </Button>
              </div>
              <div className="pinned-note-content">
                {note.title && (
                  <h3 className="pinned-note-title">{note.title}</h3>
                )}
                <p className="pinned-note-text">
                  {displayContent}
                  {shouldTruncate && !isExpanded && '...'}
                </p>
                {shouldTruncate && (
                  <button 
                    className="read-more-btn"
                    onClick={() => toggleExpanded(note.id)}
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            {note.tags && note.tags.length > 0 && (
              <div className="pinned-note-tags">
                {note.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="tag">
                    {tag}
                  </span>
                ))}
                {note.tags.length > 3 && (
                  <span className="tag-more">+{note.tags.length - 3}</span>
                )}
              </div>
            )}
            
            {/* Note Replies for Pinned Notes - Compact View */}
            <div className="pinned-note-replies">
              <NoteReplies 
                noteId={note.id}
                isSharedWithMe={false}
                currentUserId={user?.id}
                noteOwnerId={note.user_id}
                compactMode={true}
              />
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};


const SharedNotesSection = ({ notes, user }) => {
  const [expandedNotes, setExpandedNotes] = useState(new Set());
  const [isSectionCollapsed, setIsSectionCollapsed] = useState(true);
  
  if (!notes || notes.length === 0) return null;
  
  const toggleExpanded = (noteId) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(noteId)) {
      newExpanded.delete(noteId);
    } else {
      newExpanded.add(noteId);
    }
    setExpandedNotes(newExpanded);
  };
  
  return (
    <div className="shared-notes-section">
      <div 
        className="shared-notes-section-header"
        onClick={() => setIsSectionCollapsed(!isSectionCollapsed)}
      >
        <div className="section-header-content">
          <h2 className="text-lg font-semibold text-default-font">📤 Shared Notes</h2>
          {isSectionCollapsed && (
            <div className="section-summary">
              <span className="notes-count">{notes.length} {notes.length === 1 ? 'note' : 'notes'}</span>
              {notes.length > 0 && (
                <span className="notes-preview">
                  • {notes[0].title || notes[0].content.substring(0, 35) + '...'}
                  {notes[0].sender && (
                    <span className="notes-sender"> • {notes[0].sender?.display_name || notes[0].sender?.email?.split('@')[0] || 'Unknown'}</span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        <button 
          className="collapse-toggle-btn"
          type="button"
        >
          {isSectionCollapsed ? <FaChevronDown size={16} /> : <FaChevronUp size={16} />}
        </button>
      </div>
      <div className={`shared-notes-grid ${isSectionCollapsed ? 'collapsed' : ''}`}>
        {notes.map(note => {
          const isExpanded = expandedNotes.has(note.id);
          const shouldTruncate = note.content.length > 300;
          const displayContent = shouldTruncate && !isExpanded 
            ? note.content.substring(0, 300) 
            : note.content;
          
          return (
            <div key={note.id} className="shared-note-card">
              <div className="shared-note-header">
                <div className="shared-note-meta">
                  <span className="shared-indicator">
                    📤
                  </span>
                  <span className="note-date">
                    {new Date(note.shared_at || note.created_at).toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </span>
                </div>
                <div className="shared-from">
                  <span className="shared-from-label">From:</span>
                  <span className="shared-from-user">
                    {note.sender?.display_name || note.sender?.email || `User ${note.user_id?.substring(0, 8)}...` || 'Unknown User'}
                  </span>
                </div>
              </div>
              <div className="shared-note-content">
                {note.title && (
                  <h3 className="shared-note-title">{note.title}</h3>
                )}
                <p className="shared-note-text">
                  {displayContent}
                  {shouldTruncate && !isExpanded && '...'}
                </p>
                {shouldTruncate && (
                  <button 
                    className="read-more-btn"
                    onClick={() => toggleExpanded(note.id)}
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            {note.tags && note.tags.length > 0 && (
              <div className="shared-note-tags">
                {note.tags.slice(0, 3).map(tag => (
                  <span key={tag} className="tag">
                    {tag}
                  </span>
                ))}
                {note.tags.length > 3 && (
                  <span className="tag-more">+{note.tags.length - 3}</span>
                )}
              </div>
            )}
            
            {/* Note Replies for Shared Notes */}
            <NoteReplies 
              noteId={note.id}
              isSharedWithMe={true}
              currentUserId={user?.id}
              noteOwnerId={note.user_id}
            />
          </div>
          );
        })}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [activeWorkPlan, setActiveWorkPlan] = useState(null);
  const [unclosedWorkPlans, setUnclosedWorkPlans] = useState([]);
  //const [tasks, setTasks] = useState([]);
  const [workPlanTasks, setWorkPlanTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [closedTodayPlan, setClosedTodayPlan] = useState(null);
  const [editingTaskDetailsId, setEditingTaskDetailsId] = useState(null);
  const [taskDetailsValidationError, setTaskDetailsValidationError] = useState(null);
  const [workDescription, setWorkDescription] = useState('');
  const [taskHours, setTaskHours] = useState(0);
  const [taskStatus, setTaskStatus] = useState('');
  const [pinnedNotes, setPinnedNotes] = useState([]);
  const [sharedNotes, setSharedNotes] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [projectsWithOverdue, setProjectsWithOverdue] = useState(null);
  const [isManagerStatsCollapsed, setIsManagerStatsCollapsed] = useState(true);
  const [projectsWithDueSoon, setProjectsWithDueSoon] = useState(null);
  const [isDueSoonStatsCollapsed, setIsDueSoonStatsCollapsed] = useState(true);
  const [activeProjects, setActiveProjects] = useState(null);
  const [isActiveProjectsCollapsed, setIsActiveProjectsCollapsed] = useState(true);
  const [activeProjectsYesterday, setActiveProjectsYesterday] = useState(null);
  const [isActiveProjectsYesterdayCollapsed, setIsActiveProjectsYesterdayCollapsed] = useState(true);
  const [projectsLowActiveTasks, setProjectsLowActiveTasks] = useState(null);
  const [isProjectsLowActiveTasksCollapsed, setIsProjectsLowActiveTasksCollapsed] = useState(true);
  const [projectsNoActiveTasks, setProjectsNoActiveTasks] = useState(null);
  const [isProjectsNoActiveTasksCollapsed, setIsProjectsNoActiveTasksCollapsed] = useState(true);
  const [healthyProjects, setHealthyProjects] = useState(null);
  const [isHealthyProjectsCollapsed, setIsHealthyProjectsCollapsed] = useState(true);
  const [resourcesLowTasks, setResourcesLowTasks] = useState(null);
  const [isResourcesLowTasksCollapsed, setIsResourcesLowTasksCollapsed] = useState(true);
  const [resourcesSameTaskLong, setResourcesSameTaskLong] = useState(null);
  const [isResourcesSameTaskLongCollapsed, setIsResourcesSameTaskLongCollapsed] = useState(true);
  const [resourcesTotalTasks, setResourcesTotalTasks] = useState(null);
  const [tasksHighActivity, setTasksHighActivity] = useState(null);
  const [isTasksHighActivityCollapsed, setIsTasksHighActivityCollapsed] = useState(true);
  const [resourcesTodayTasks, setResourcesTodayTasks] = useState(null);
  const [isResourcesTodayTasksCollapsed, setIsResourcesTodayTasksCollapsed] = useState(true);
  const [resourcesWorkPlanStatus, setResourcesWorkPlanStatus] = useState(null);
  const [isResourcesWorkPlanStatusCollapsed, setIsResourcesWorkPlanStatusCollapsed] = useState(true);
  const [tasksCompletedThisWeek, setTasksCompletedThisWeek] = useState(null);
  const [isTasksCompletedThisWeekCollapsed, setIsTasksCompletedThisWeekCollapsed] = useState(true);
  const [resourcesOnLeave, setResourcesOnLeave] = useState(null);
  const [isResourcesOnLeaveCollapsed, setIsResourcesOnLeaveCollapsed] = useState(true);
  const [managerGoals, setManagerGoals] = useState([]);
  const [managerGoalsSummary, setManagerGoalsSummary] = useState(null);
  const [loadingManagerGoals, setLoadingManagerGoals] = useState(false);
  const [isManagerGoalsCollapsed, setIsManagerGoalsCollapsed] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showAttachments, setShowAttachments] = useState(null);
  const [taskGoals, setTaskGoals] = useState({});
  const [loadingGoals, setLoadingGoals] = useState(false);
  const [expandedGoalTaskId, setExpandedGoalTaskId] = useState(null);
  const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
  const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);
  const [selectedTaskForModal, setSelectedTaskForModal] = useState(null);
  const [isTaskSummaryModalOpen, setIsTaskSummaryModalOpen] = useState(false);
  const [allTasks, setAllTasks] = useState([]);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
      loadUserRole();
    }
  }, [user?.id]);

  useEffect(() => {
    if (userRole && isManager(userRole)) {
      loadManagerStats();
      loadDueSoonStats();
      loadActiveProjects();
      loadActiveProjectsYesterday();
      loadProjectsLowActiveTasks();
      loadProjectsNoActiveTasks();
      loadHealthyProjects();
      loadResourcesLowTasks();
      loadResourcesSameTaskLong();
      loadResourcesTotalTasks();
      loadTasksHighActivity();
      loadResourcesTodayTasks();
      loadResourcesWorkPlanStatus();
      loadTasksCompletedThisWeek();
      loadResourcesOnLeave();
      loadManagerGoals();
    }
  }, [userRole]);

  const loadUserRole = async () => {
    try {
      const response = await userApi.getRole();
      // Handle both response formats: { role: 'manager' } or just 'manager'
      const role = (response?.role || response || '').toString().trim().toLowerCase();
      setUserRole(role);
    } catch (error) {
      console.error('Failed to load user role:', error);
      setUserRole(null);
    }
  };

  const loadManagerStats = async () => {
    try {
      const response = await api.get('/api/manager/projects-with-overdue');
      const data = Array.isArray(response.data) ? response.data : [];
      setProjectsWithOverdue(data);
    } catch (error) {
      console.error('Failed to load manager stats:', error);
      setProjectsWithOverdue([]);
    }
  };

  const loadDueSoonStats = async () => {
    try {
      const response = await api.get('/api/manager/projects-with-due-soon');
      const data = Array.isArray(response.data) ? response.data : [];
      setProjectsWithDueSoon(data);
    } catch (error) {
      console.error('Failed to load due soon stats:', error);
      setProjectsWithDueSoon([]);
    }
  };

  const loadActiveProjects = async () => {
    try {
      const response = await api.get('/api/manager/projects-active', { params: { days: 7 } });
      const data = Array.isArray(response.data) ? response.data : [];
      // Transform data to match OverdueCards format
      const transformedData = data.map(project => ({
        id: project.id,
        name: project.name,
        abbreviation: project.abbreviation,
        activeCount: project.activeCount,
        activeItems: project.activityItems
      }));
      setActiveProjects(transformedData);
    } catch (error) {
      console.error('Failed to load active projects:', error);
      setActiveProjects([]);
    }
  };

  const loadActiveProjectsYesterday = async () => {
    try {
      const response = await api.get('/api/manager/projects-active', { params: { days: 1 } });
      const data = Array.isArray(response.data) ? response.data : [];
      // Transform data to match OverdueCards format
      const transformedData = data.map(project => ({
        id: project.id,
        name: project.name,
        abbreviation: project.abbreviation,
        activeCount: project.activeCount,
        activeItems: project.activityItems
      }));
      setActiveProjectsYesterday(transformedData);
    } catch (error) {
      console.error('Failed to load active projects yesterday:', error);
      setActiveProjectsYesterday([]);
    }
  };

  const loadProjectsLowActiveTasks = async () => {
    try {
      const response = await api.get('/api/manager/projects-by-active-task-count', { params: { threshold: 7 } });
      const data = Array.isArray(response.data) ? response.data : [];
      // Transform API response to match UI field names
      const transformedData = data.map(project => ({
        id: project.id,
        name: project.name,
        abbreviation: project.abbreviation,
        lowTaskCount: project.activeTaskCount,
        lowTaskItems: project.taskItems
      }));
      setProjectsLowActiveTasks(transformedData);
    } catch (error) {
      console.error('Failed to load projects with low active tasks:', error);
      setProjectsLowActiveTasks([]);
    }
  };

  const loadProjectsNoActiveTasks = async () => {
    try {
      const response = await api.get('/api/manager/projects-by-active-task-count', { params: { threshold: 0 } });
      const data = Array.isArray(response.data) ? response.data : [];
      // Transform API response to match UI field names
      const transformedData = data.map(project => ({
        id: project.id,
        name: project.name,
        abbreviation: project.abbreviation,
        noTaskCount: project.activeTaskCount,
        noTaskItems: project.taskItems
      }));
      setProjectsNoActiveTasks(transformedData);
    } catch (error) {
      console.error('Failed to load projects with no active tasks:', error);
      setProjectsNoActiveTasks([]);
    }
  };

  const loadHealthyProjects = async () => {
    try {
      const response = await api.get('/api/manager/projects-by-active-task-count', { params: { minThreshold: 10 } });
      const data = Array.isArray(response.data) ? response.data : [];
      // Transform API response to match UI field names
      const transformedData = data.map(project => ({
        id: project.id,
        name: project.name,
        abbreviation: project.abbreviation,
        healthyTaskCount: project.activeTaskCount,
        healthyTaskItems: project.taskItems
      }));
      setHealthyProjects(transformedData);
    } catch (error) {
      console.error('Failed to load healthy projects:', error);
      setHealthyProjects([]);
    }
  };

  const loadResourcesLowTasks = async () => {
    try {
      const response = await api.get('/api/manager/resources-low-tasks');
      const data = Array.isArray(response.data) ? response.data : [];
      setResourcesLowTasks(data);
    } catch (error) {
      console.error('Failed to load resources with low tasks:', error);
      setResourcesLowTasks([]);
    }
  };

  const loadResourcesSameTaskLong = async () => {
    try {
      const response = await api.get('/api/manager/resources-same-task-long');
      const data = Array.isArray(response.data) ? response.data : [];
      setResourcesSameTaskLong(data);
    } catch (error) {
      console.error('Failed to load resources on same task long:', error);
      setResourcesSameTaskLong([]);
    }
  };

  const loadResourcesTotalTasks = async () => {
    try {
      const response = await api.get('/api/manager/resources-total-tasks');
      setResourcesTotalTasks(response.data);
    } catch (error) {
      console.error('Failed to load total tasks:', error);
      setResourcesTotalTasks(null);
    }
  };

  const loadTasksHighActivity = async () => {
    try {
      const response = await api.get('/api/manager/tasks-high-activity');
      const data = Array.isArray(response.data) ? response.data : [];
      setTasksHighActivity(data);
    } catch (error) {
      console.error('Failed to load tasks with high activity:', error);
      setTasksHighActivity([]);
    }
  };

  const loadResourcesTodayTasks = async () => {
    try {
      const response = await api.get('/api/manager/resources-today-tasks');
      const data = Array.isArray(response.data) ? response.data : [];
      setResourcesTodayTasks(data);
    } catch (error) {
      console.error('Failed to load resources today tasks:', error);
      setResourcesTodayTasks([]);
    }
  };

  const loadResourcesWorkPlanStatus = async () => {
    try {
      const response = await api.get('/api/manager/resources-workplan-status');
      setResourcesWorkPlanStatus(response.data);
    } catch (error) {
      console.error('Failed to load resources work plan status:', error);
      setResourcesWorkPlanStatus(null);
    }
  };

  const loadTasksCompletedThisWeek = async () => {
    try {
      const response = await api.get('/api/manager/tasks-completed-this-week');
      const data = response.data?.completedTasks || [];
      setTasksCompletedThisWeek(data);
    } catch (error) {
      console.error('Failed to load tasks completed this week:', error);
      setTasksCompletedThisWeek([]);
    }
  };

  const loadResourcesOnLeave = async () => {
    try {
      const response = await api.get('/api/manager/resources-on-leave');
      const data = Array.isArray(response.data) ? response.data : [];
      setResourcesOnLeave(data);
    } catch (error) {
      console.error('Failed to load resources on leave:', error);
      setResourcesOnLeave([]);
    }
  };

  const loadManagerGoals = async () => {
    try {
      setLoadingManagerGoals(true);
      const today = new Date().toISOString().split('T')[0];
      const data = await dailyReviewService.getAllGoals(today);
      setManagerGoals(data.goals || []);
      setManagerGoalsSummary(data.summary || null);
    } catch (error) {
      console.error('Failed to load manager goals:', error);
      setManagerGoals([]);
      setManagerGoalsSummary(null);
    } finally {
      setLoadingManagerGoals(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const [todayClosedPlan, workPlan, unclosedPlans, allTasks, pinnedNotesData, sharedNotesData] = await Promise.all([
        workPlanApi.getTodayClosed(),
        workPlanApi.getActive(),
        workPlanApi.getUnclosed(),
        taskApi.getAll(),
        api.get('/api/scribble/pinned').then(res => res.data).catch(() => []),
        api.get('/api/scribble/shared').then(res => res.data).catch(() => [])
      ]);

      setClosedTodayPlan(todayClosedPlan || null);
      setActiveWorkPlan(workPlan || null);
      setUnclosedWorkPlans(Array.isArray(unclosedPlans) ? unclosedPlans : []);
      setPinnedNotes(Array.isArray(pinnedNotesData) ? pinnedNotesData : []);
      setSharedNotes(Array.isArray(sharedNotesData) ? sharedNotesData : []);
      setAllTasks(Array.isArray(allTasks) ? allTasks : []);
      //setTasks(Array.isArray(allTasks) ? allTasks : []);

      // Combine work plan tasks with their full task data
      if (workPlan && workPlan.tasks && Array.isArray(workPlan.tasks)) {
        const combinedTasks = workPlan.tasks.map(planTask => {
          const taskData = allTasks?.find(t => t.id === planTask.task_id);
          return taskData ? {
            ...taskData,
            ...planTask,
            id: planTask.task_id,
            workPlanTaskId: planTask.work_plan_task_id,
            description: taskData.description,
            notes: planTask.notes || '',
            status: planTask.status || TASK_STATUS.OPEN,
            actualHours: planTask.hours || 0,
            plannedHours: planTask.planned_hours || 0,
            project_name: taskData.project?.name || null,
            work_plan_attachments: planTask.work_plan_attachments || [],
            // Explicitly preserve task creation date to ensure accurate age calculation
            created_at: taskData.created_at,
            created_by: taskData.created_by
          } : null;
        }).filter(Boolean);
        setWorkPlanTasks(combinedTasks);
      } else {
        setWorkPlanTasks([]); // Set empty array if no tasks
      }

      // Load goals after tasks are loaded
      if (workPlan && workPlan.tasks && workPlan.tasks.length > 0) {
        loadTaskGoals();
      }
    } catch (error) {
      console.error('Failed to load work plan:', error);
      setError('Failed to load work plan data');
    } finally {
      setLoading(false);
    }
  };

  const loadTaskGoals = async () => {
    try {
      setLoadingGoals(true);
      const today = new Date().toISOString().split('T')[0];
      console.log('Loading goals for date:', today);
      const response = await taskApi.getUserGoals(today);
      console.log('Goals response:', response);
      setTaskGoals(response.goals || {});
      console.log('Set task goals:', response.goals || {});
    } catch (error) {
      console.error('Failed to load task goals:', error);
      // Don't set error state - goals are optional
      setTaskGoals({});
    } finally {
      setLoadingGoals(false);
    }
  };

  const handleCreateWorkPlan = () => {
    navigate('/work-plan');
  };

  // Helper functions for TaskSummaryModal
  const calculateTaskSummary = useCallback((task) => {
    if (!task.work_plan_tasks || task.work_plan_tasks.length === 0) {
      return { totalHours: 0, occurrences: 0, work_plan_attachments: [] };
    }
    const totalHours = task.work_plan_tasks.reduce((sum, wpt) => sum + (parseFloat(wpt.hours) || 0), 0);
    const occurrences = task.work_plan_tasks.length;
    const attachments = [];
    task.work_plan_tasks.forEach(wpt => {
      if (wpt.work_plan_attachments && wpt.work_plan_attachments.length > 0) {
        attachments.push(...wpt.work_plan_attachments);
      }
    });
    return { totalHours: totalHours.toFixed(1), occurrences, work_plan_attachments: attachments };
  }, []);

  const getTaskStatus = useCallback((taskId) => {
    const task = allTasks.find(t => t.id === taskId) || workPlanTasks.find(t => t.id === taskId);
    return task?.status || 'open';
  }, [allTasks, workPlanTasks]);

  const getStatusVariant = useCallback((status) => {
    switch (status?.toLowerCase()) {
      case 'in-progress': return 'warning';
      case 'completed': return 'success';
      case 'released': return 'success';
      case 'open': return 'neutral';
      default: return 'neutral';
    }
  }, []);

  const getTaskHistory = useCallback((taskId) => {
    const task = allTasks.find(t => t.id === taskId) || workPlanTasks.find(t => t.id === taskId);
    if (!task || !task.work_plan_tasks || task.work_plan_tasks.length === 0) {
      return [];
    }
    return task.work_plan_tasks
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .map(wpt => ({
        date: new Date(wpt.created_at),
        hours: wpt.hours || 0,
        notes: wpt.notes || '',
        status: wpt.status === 'completed' ? 'Completed' : 'In Progress',
        attachments: wpt.work_plan_attachments || []
      }));
  }, [allTasks, workPlanTasks]);

  const openTaskSummaryModal = useCallback(async (task) => {
    try {
      // Fetch full task details with work_plan_tasks
      const fullTask = await taskApi.getById(task.id);
      const summary = calculateTaskSummary(fullTask);
      setSelectedTaskForModal({ ...fullTask, ...summary });
      setIsTaskSummaryModalOpen(true);
    } catch (error) {
      console.error('Failed to load task details:', error);
      // Fallback to using the task data we have
      const summary = calculateTaskSummary(task);
      setSelectedTaskForModal({ ...task, ...summary });
      setIsTaskSummaryModalOpen(true);
    }
  }, [calculateTaskSummary]);

  const closeTaskSummaryModal = useCallback(() => {
    setSelectedTaskForModal(null);
    setIsTaskSummaryModalOpen(false);
  }, []);

  const handleFeatureTagClick = (e, featureTag) => {
    e.stopPropagation(); // Prevent event bubbling
    if (featureTag && featureTag.id) {
      setSelectedFeatureTag(featureTag);
      setIsFeatureTagModalOpen(true);
    }
  };

  // Get unique users from workPlanTasks for the feature tag modal
  const uniqueUsers = React.useMemo(() => {
    const usersMap = new Map();
    workPlanTasks.forEach(task => {
      if (task.assigned_to?.id) {
        usersMap.set(task.assigned_to.id, task.assigned_to);
      }
      if (task.created_by?.id) {
        usersMap.set(task.created_by.id, task.created_by);
      }
    });
    return Array.from(usersMap.values());
  }, [workPlanTasks]);

  const handleCloseWorkDay = () => {
    navigate('/close-work-day');
  };

  const handleAddToWorkPlan = () => {
    navigate('/add-to-work-plan');
  };

  const handleCloseOldWorkPlan = (workPlanId) => {
    navigate(`/close-work-day/${workPlanId}`);
  };

  const handleWorkDescriptionChange = (value) => {
    setWorkDescription(value);
  };

  const handleHoursChange = (value) => {
    setTaskHours(value);
  };

  const handleStatusChange = (value) => {
    setTaskStatus(value);
  };

  const handleTaskDetailsSave = async (taskId) => {
    try {
      setTaskDetailsValidationError(null);

      // For members, hours must be > 0 when marking a task as completed
      if (isMember(userRole) && taskStatus === TASK_STATUS.COMPLETED && (taskHours <= 0 || !taskHours)) {
        setTaskDetailsValidationError('Please log hours before marking the task as completed.');
        return;
      }

      const workPlanTask = workPlanTasks.find(t => t.id === taskId);
      if (!workPlanTask) return;

      const updatedTask = await taskApi.update(taskId, {
        work_plan_task_id: workPlanTask.workPlanTaskId,
        notes: workDescription,
        hours: taskHours,
        status: taskStatus
      });

      // Update the workPlanTasks array with all the new values
      setWorkPlanTasks(workPlanTasks.map(t => {
        if (t.id === taskId) {
          return {
            ...t,
            notes: workDescription,
            hours: taskHours,
            status: taskStatus,
            status_task: taskStatus
          };
        }
        return t;
      }));
      
      setEditingTaskDetailsId(null);
      setTaskDetailsValidationError(null);
      setWorkDescription('');
      setTaskHours(0);
      setTaskStatus('');
    } catch (err) {
      console.error('Error updating task details:', err);
      setError('Failed to update task details');
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  const canModifyWorkPlan = activeWorkPlan && !activeWorkPlan.isSubmitted && !activeWorkPlan.isClosed;

  // Configuration for overdue stats section
  const overdueStatsConfig = {
    emptyStateCollapsed: '✓ All projects on track',
    emptyStateExpanded: 'No projects with overdue features',
    badgeClassName: 'overdue-badge-inline',
    badgeLabel: 'overdue features',
    countFieldName: 'overdueCount',
    featuresFieldName: 'overdueFeatures',
    daysFieldName: 'days_overdue',
    getProjectUrgency: (features) => {
      const maxDays = Math.max(...features.map(f => f.days_overdue || 0));
      return maxDays > 30 ? 'critical' : maxDays > 14 ? 'high' : 'medium';
    },
    getFeatureUrgency: (feature) => {
      const days = feature.days_overdue || 0;
      return days > 30 ? 'critical' : days > 14 ? 'high' : 'medium';
    },
    formatDays: (days) => `${days}d`,
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">affected by {totalCount} {totalCount === 1 ? 'feature' : 'features'}</span>
        </>
      );
    }
  };

  // Configuration for due soon stats section
  const dueSoonStatsConfig = {
    emptyStateCollapsed: '✓ No features due soon',
    emptyStateExpanded: 'No features due in the next 5 days',
    badgeClassName: 'due-soon-badge-inline',
    badgeLabel: 'features due soon',
    countFieldName: 'dueSoonCount',
    featuresFieldName: 'dueSoonFeatures',
    daysFieldName: 'days_until_due',
    additionalSectionClass: 'due-soon-section',
    getProjectUrgency: (features) => {
      const minDays = Math.min(...features.map(f => f.days_until_due || 0));
      return minDays <= 1 ? 'critical' : minDays <= 3 ? 'high' : 'medium';
    },
    getFeatureUrgency: (feature) => {
      const days = feature.days_until_due || 0;
      return days <= 1 ? 'critical' : days <= 3 ? 'high' : 'medium';
    },
    formatDays: (days) => `${days}d`,
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">projects - {totalCount} {totalCount === 1 ? 'feature' : 'features'} due soon</span>
        </>
      );
    }
  };

  // Configuration for active projects stats section
  const activeProjectsConfig = {
    emptyStateCollapsed: '✓ No active projects',
    emptyStateExpanded: 'No projects with activity in the last 7 days',
    badgeClassName: 'active-badge-inline',
    badgeLabel: 'active projects',
    countFieldName: 'activeCount',
    featuresFieldName: 'activeItems',
    daysFieldName: 'days_since_activity',
    additionalSectionClass: 'active-projects-section',
    getProjectUrgency: (features) => {
      // More recent activity = lower urgency (we want to show recent activity as positive)
      const maxDays = Math.max(...features.map(f => f.days_since_activity || 0));
      return maxDays <= 1 ? 'medium' : maxDays <= 3 ? 'high' : 'critical';
    },
    getFeatureUrgency: (feature) => {
      const days = feature.days_since_activity || 0;
      return days <= 1 ? 'medium' : days <= 3 ? 'high' : 'critical';
    },
    formatDays: (days) => `${days}d ago`,
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">projects active</span>
        </>
      );
    }
  };

  // Configuration for active projects yesterday stats section
  const activeProjectsYesterdayConfig = {
    emptyStateCollapsed: '✓ No activity yesterday',
    emptyStateExpanded: 'No projects with activity yesterday',
    badgeClassName: 'active-badge-inline',
    badgeLabel: 'projects active',
    countFieldName: 'activeCount',
    featuresFieldName: 'activeItems',
    daysFieldName: 'days_since_activity',
    additionalSectionClass: 'active-projects-yesterday-section',
    getProjectUrgency: (features) => {
      return 'medium';
    },
    getFeatureUrgency: (feature) => {
      return 'medium';
    },
    formatDays: (days) => `${days}d ago`,
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">projects active yesterday</span>
        </>
      );
    }
  };

  // Configuration for projects with low active tasks (< 7)
  const projectsLowActiveTasksConfig = {
    emptyStateCollapsed: '✓ All projects have sufficient tasks',
    emptyStateExpanded: 'All projects have 7 or more active tasks',
    badgeClassName: 'low-tasks-badge-inline',
    badgeLabel: 'projects need attention',
    countFieldName: 'lowTaskCount',
    featuresFieldName: 'lowTaskItems',
    daysFieldName: 'task_count',
    additionalSectionClass: 'low-active-tasks-section',
    getProjectUrgency: (features) => {
      const taskCount = features[0]?.task_count || 0;
      return taskCount === 0 ? 'critical' : taskCount <= 2 ? 'high' : 'medium';
    },
    getFeatureUrgency: (feature) => {
      const taskCount = feature.task_count || 0;
      return taskCount === 0 ? 'critical' : taskCount <= 2 ? 'high' : 'medium';
    },
    formatDays: (taskCount) => `${taskCount} task${taskCount !== 1 ? 's' : ''}`,
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">projects need more tasks</span>
        </>
      );
    }
  };

  // Configuration for projects with no active tasks (high alert)
  const projectsNoActiveTasksConfig = {
    emptyStateCollapsed: '✓ All projects have active tasks',
    emptyStateExpanded: 'All projects have at least one active task',
    badgeClassName: 'no-tasks-badge-inline',
    badgeLabel: 'projects need attention',
    countFieldName: 'noTaskCount',
    featuresFieldName: 'noTaskItems',
    daysFieldName: 'task_count',
    additionalSectionClass: 'no-active-tasks-section',
    getProjectUrgency: (features) => {
      return 'critical'; // Always critical for no tasks
    },
    getFeatureUrgency: (feature) => {
      return 'critical'; // Always critical for no tasks
    },
    formatDays: () => 'No tasks',
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">projects with no active tasks</span>
        </>
      );
    }
  };

  // Configuration for healthy projects (> 10 active tasks)
  const healthyProjectsConfig = {
    emptyStateCollapsed: 'No projects with > 10 active tasks',
    emptyStateExpanded: 'No projects currently have more than 10 active tasks',
    badgeClassName: 'healthy-badge-inline',
    badgeLabel: 'healthy projects',
    countFieldName: 'healthyTaskCount',
    featuresFieldName: 'healthyTaskItems',
    daysFieldName: 'task_count',
    additionalSectionClass: 'healthy-projects-section',
    getProjectUrgency: (features) => {
      return 'low'; // Healthy projects have low urgency (good status)
    },
    getFeatureUrgency: (feature) => {
      return 'low'; // Healthy projects have low urgency (good status)
    },
    formatDays: (taskCount) => `${taskCount} task${taskCount !== 1 ? 's' : ''}`,
    formatBadgeMessage: (totalCount, projectCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{projectCount}</span>
          <span className="badge-detail-text">projects with &gt; 10 active tasks</span>
        </>
      );
    }
  };

  // Configuration for resources with low tasks (< 2 tasks this week)
  const resourcesLowTasksConfig = {
    emptyStateCollapsed: '✓ All resources have sufficient tasks',
    emptyStateExpanded: 'All resources are working on 2 or more tasks this week',
    badgeClassName: 'low-tasks-badge-inline',
    badgeLabel: 'resources need more tasks',
    countFieldName: 'lowTaskCount',
    featuresFieldName: 'resourceItems',
    daysFieldName: 'task_count',
    additionalSectionClass: 'resources-low-tasks-section',
    itemLabel: 'resource',
    getProjectUrgency: (features) => {
      const taskCount = features[0]?.task_count || 0;
      return taskCount === 0 ? 'critical' : 'high';
    },
    getFeatureUrgency: (feature) => {
      const taskCount = feature.task_count || 0;
      return taskCount === 0 ? 'critical' : 'high';
    },
    formatDays: (taskCount) => `${taskCount} task${taskCount !== 1 ? 's' : ''}`,
    formatBadgeMessage: (totalCount, resourceCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{resourceCount}</span>
          <span className="badge-detail-text">resources with &lt; 2 tasks</span>
        </>
      );
    }
  };

  // Configuration for resources on same task for > 3 days
  const resourcesSameTaskLongConfig = {
    emptyStateCollapsed: '✓ No resources stuck on same task',
    emptyStateExpanded: 'No resources are working on the same task for more than 3 days',
    badgeClassName: 'same-task-long-badge-inline',
    badgeLabel: 'resources need attention',
    countFieldName: 'longTaskCount',
    featuresFieldName: 'resourceItems',
    daysFieldName: 'days_count',
    additionalSectionClass: 'resources-same-task-long-section',
    itemLabel: 'resource',
    getProjectUrgency: (features) => {
      const maxDays = Math.max(...features.map(f => f.days_count || 0));
      return maxDays > 5 ? 'critical' : 'high';
    },
    getFeatureUrgency: (feature) => {
      const days = feature.days_count || 0;
      return days > 5 ? 'critical' : 'high';
    },
    formatDays: (daysCount) => `${daysCount} day${daysCount !== 1 ? 's' : ''}`,
    formatBadgeMessage: (totalCount, resourceCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{resourceCount}</span>
          <span className="badge-detail-text">resources on same task &gt; 3 days</span>
        </>
      );
    }
  };

  // Configuration for tasks with high activity (> 5 times worked)
  const tasksHighActivityConfig = {
    emptyStateCollapsed: '✓ No tasks worked more than 5 times',
    emptyStateExpanded: 'No tasks have been worked on more than 5 times this week',
    badgeClassName: 'high-activity-badge-inline',
    badgeLabel: 'tasks need attention',
    countFieldName: 'highActivityCount',
    featuresFieldName: 'resourceItems',
    daysFieldName: 'work_plan_task_count',
    additionalSectionClass: 'tasks-high-activity-section',
    itemLabel: 'task',
    getProjectUrgency: (features) => {
      const maxCount = Math.max(...features.map(f => f.work_plan_task_count || 0));
      return maxCount > 10 ? 'critical' : maxCount > 7 ? 'high' : 'medium';
    },
    getFeatureUrgency: (feature) => {
      const count = feature.work_plan_task_count || 0;
      return count > 10 ? 'critical' : count > 7 ? 'high' : 'medium';
    },
    formatDays: (workPlanTaskCount) => `${workPlanTaskCount} times`,
    formatBadgeMessage: (totalCount, taskCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{taskCount}</span>
          <span className="badge-detail-text">tasks worked &gt; 5 times</span>
        </>
      );
    }
  };

  // Configuration for resources today tasks
  const resourcesTodayTasksConfig = {
    emptyStateCollapsed: 'No work plans for today',
    emptyStateExpanded: 'No resources have work plans for today',
    badgeClassName: 'today-tasks-badge-inline',
    badgeLabel: 'resources with tasks today',
    countFieldName: 'todayTaskCount',
    featuresFieldName: 'resourceItems',
    daysFieldName: 'task_count',
    additionalSectionClass: 'resources-today-tasks-section',
    itemLabel: 'resource',
    getProjectUrgency: (features) => {
      const taskCount = features[0]?.task_count || 0;
      return taskCount === 0 ? 'critical' : taskCount <= 2 ? 'high' : 'medium';
    },
    getFeatureUrgency: (feature) => {
      const taskCount = feature.task_count || 0;
      return taskCount === 0 ? 'critical' : taskCount <= 2 ? 'high' : 'medium';
    },
    formatDays: (taskCount) => `${taskCount} task${taskCount !== 1 ? 's' : ''}`,
    formatBadgeMessage: (totalCount, resourceCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{resourceCount}</span>
          <span className="badge-detail-text">resources with tasks today</span>
        </>
      );
    }
  };

  // Configuration for resources work plan status
  const resourcesWorkPlanStatusConfig = {
    emptyStateCollapsed: '✓ All resources started work plans',
    emptyStateExpanded: 'All resources have created their work plans for today',
    badgeClassName: 'workplan-status-badge-inline',
    badgeLabel: 'resources started work plan',
    countFieldName: 'missingWorkPlanCount',
    featuresFieldName: 'resourceItems',
    daysFieldName: 'task_count',
    additionalSectionClass: 'resources-workplan-status-section',
    itemLabel: 'resource',
    getProjectUrgency: (features) => {
      return 'high'; // Always high urgency for missing work plans
    },
    getFeatureUrgency: (feature) => {
      return 'high';
    },
    formatDays: (taskCount) => 'Missing',
    formatBadgeMessage: (totalCount, resourceCount, badgeLabel) => {
      // Access resourcesWorkPlanStatus from closure
      const status = resourcesWorkPlanStatus;
      if (!status) {
        return <span>Loading...</span>;
      }
      const { totalResources, resourcesWithWorkPlans } = status;
      return (
        <>
          <span className="badge-count-prominent">{resourcesWithWorkPlans || 0}/{totalResources || 0}</span>
          <span className="badge-detail-text">resources started work plan</span>
        </>
      );
    },
    // Custom isEmpty function: show empty state only when all resources have started (no missing resources)
    // This should return true only when all resources have work plans AND there are no missing resources
    isEmpty: () => {
      if (!resourcesWorkPlanStatus) return false;
      const { resourcesWithWorkPlans, totalResources, resourcesMissing } = resourcesWorkPlanStatus;
      // Return true only if: totalResources > 0, all have work plans, and no missing resources
      return totalResources > 0 && resourcesWithWorkPlans === totalResources && resourcesMissing === 0;
    }
  };

  // Configuration for resources on leave
  const resourcesOnLeaveConfig = {
    emptyStateCollapsed: '✓ No resources on leave today',
    emptyStateExpanded: 'No resources are on leave today',
    badgeClassName: 'on-leave-badge-inline',
    badgeLabel: 'resources on leave',
    countFieldName: 'leaveCount',
    featuresFieldName: 'resourceItems',
    daysFieldName: 'days_count',
    additionalSectionClass: 'resources-on-leave-section',
    itemLabel: 'resource',
    getProjectUrgency: (features) => {
      return 'medium'; // Medium urgency - informational
    },
    getFeatureUrgency: (feature) => {
      return 'medium'; // Medium urgency - informational
    },
    formatDays: (daysCount) => `${daysCount} day${daysCount !== 1 ? 's' : ''}`,
    formatBadgeMessage: (totalCount, resourceCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{resourceCount}</span>
          <span className="badge-detail-text">resources on leave today</span>
        </>
      );
    }
  };

  // Configuration for tasks completed this week
  const tasksCompletedThisWeekConfig = {
    emptyStateCollapsed: 'No tasks completed this week',
    emptyStateExpanded: 'No tasks have been moved to completed status this week',
    badgeClassName: 'completed-tasks-badge-inline',
    badgeLabel: 'tasks completed',
    countFieldName: 'completedCount',
    featuresFieldName: 'completedItems',
    daysFieldName: 'completion_count',
    additionalSectionClass: 'tasks-completed-this-week-section',
    itemLabel: 'task',
    getProjectUrgency: (features) => {
      return 'low'; // Completed tasks are positive, so low urgency (good status)
    },
    getFeatureUrgency: (feature) => {
      return 'low'; // Completed tasks are positive
    },
    formatDays: (completionCount) => `${completionCount} ${completionCount === 1 ? 'time' : 'times'}`,
    formatBadgeMessage: (totalCount, taskCount, badgeLabel) => {
      return (
        <>
          <span className="badge-count-prominent">{taskCount}</span>
          <span className="badge-detail-text">tasks completed this week</span>
        </>
      );
    }
  };

  // Render Today's Goals Section (separate, prominent section)
  const renderTodaysGoals = () => {
    if (!userRole || !isManager(userRole)) return null;

    return (
      <div className="todays-goals-wrapper">
        <div className="manager-stats-section manager-goals-section">
            <div 
              className="manager-stats-header"
              onClick={() => setIsManagerGoalsCollapsed(!isManagerGoalsCollapsed)}
            >
              <div className="section-header-content">
                <h2 className="text-lg font-semibold text-default-font">🎯 Today's Goals</h2>
                {isManagerGoalsCollapsed && managerGoalsSummary && (
                  <div className="section-summary">
                    <span className="goals-summary-badge">
                      {managerGoalsSummary.total} goals total ({managerGoalsSummary.achieved} achieved, {managerGoalsSummary.notAchieved} not achieved)
                    </span>
                  </div>
                )}
              </div>
              <button className="collapse-toggle-btn">
                {isManagerGoalsCollapsed ? <FaChevronDown /> : <FaChevronUp />}
              </button>
            </div>
            {!isManagerGoalsCollapsed && (
              <div className="manager-goals-content">
                {loadingManagerGoals ? (
                  <div className="loading-state">Loading goals...</div>
                ) : managerGoals.length === 0 ? (
                  <div className="empty-state">No goals set for today</div>
                ) : (
                  <>
                    {managerGoalsSummary && (
                      <div className="goals-summary">
                        <div className="goals-summary-stats">
                          <span><strong>Total:</strong> {managerGoalsSummary.total}</span>
                          <span><strong>Task Goals:</strong> {managerGoalsSummary.taskGoals}</span>
                          <span><strong>Feature Goals:</strong> {managerGoalsSummary.featureGoals}</span>
                          <span><strong>Personal Goals:</strong> {managerGoalsSummary.personalGoals}</span>
                        </div>
                        <div className="goals-summary-status">
                          <span className="status-achieved">✓ {managerGoalsSummary.achieved} achieved</span>
                          <span className="status-not-achieved">✗ {managerGoalsSummary.notAchieved} not achieved</span>
                        </div>
                      </div>
                    )}
                    <div className="goals-list">
                      {managerGoals.map((goal, index) => (
                        <div key={index} className={`goal-item goal-${goal.type} goal-status-${goal.status}`}>
                          <div className="goal-item-header">
                            <div className={`goal-type-badge goal-type-${goal.type}`}>
                              {goal.type === 'task' ? '📋' : goal.type === 'feature' ? '🎯' : '👤'}
                              <span className="goal-type-label">
                                {goal.type === 'task' ? 'Task' : goal.type === 'feature' ? 'Feature' : 'Personal'}
                              </span>
                            </div>
                            <div className="goal-status-indicator">
                              {goal.status === 'achieved' ? (
                                <FeatherCheckCircle size={16} className="goal-status-icon goal-achieved" />
                              ) : goal.status === 'not-achieved' ? (
                                <FeatherAlertCircle size={16} className="goal-status-icon goal-not-achieved" />
                              ) : (
                                <FeatherClock size={16} className="goal-status-icon goal-pending" />
                              )}
                              <span className={`goal-status-text status-${goal.status}`}>
                                {goal.status === 'achieved' ? 'Achieved' : goal.status === 'not-achieved' ? 'Not Achieved' : 'Pending'}
                              </span>
                            </div>
                          </div>
                          <div className="goal-item-content">
                            {goal.type === 'task' && (
                              <div className="goal-text">
                                {goal.displayId && `[${goal.displayId}] `}
                                {goal.taskTitle}
                              </div>
                            )}
                            {goal.type === 'feature' && (
                              <div className="goal-text">{goal.featureName}</div>
                            )}
                            {goal.type === 'personal' && (
                              <div className="goal-text">{goal.text}</div>
                            )}
                            <div className="goal-project">
                              <ProjectBadge 
                                projectName={goal.projectName}
                                abbreviation={goal.projectAbbreviation}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
    );
  };

  // Render Manager Stats Section (reusable component)
  const renderManagerStats = () => {
    if (!userRole || !isManager(userRole)) return null;

    return (
      <>
        <div className="manager-stats-wrapper">
          <OverdueCards 
            title="📋 Resources Vs Tasks - Today"
            data={resourcesTodayTasks} 
            navigate={null}
            isCollapsed={isResourcesTodayTasksCollapsed}
            onToggleCollapse={() => setIsResourcesTodayTasksCollapsed(!isResourcesTodayTasksCollapsed)}
            config={resourcesTodayTasksConfig}
          />
          <OverdueCards 
            title="📊 Work Plan Status - Today"
            data={resourcesWorkPlanStatus?.missingResources ?? []} 
            navigate={null}
            isCollapsed={isResourcesWorkPlanStatusCollapsed}
            onToggleCollapse={() => setIsResourcesWorkPlanStatusCollapsed(!isResourcesWorkPlanStatusCollapsed)}
            config={resourcesWorkPlanStatusConfig}
          />
          <OverdueCards 
            title="✅ Tasks Completed This Week"
            data={tasksCompletedThisWeek} 
            navigate={null}
            isCollapsed={isTasksCompletedThisWeekCollapsed}
            onToggleCollapse={() => setIsTasksCompletedThisWeekCollapsed(!isTasksCompletedThisWeekCollapsed)}
            config={tasksCompletedThisWeekConfig}
          />
          <OverdueCards 
            title="🏖️ Resources on Leave - Today"
            data={resourcesOnLeave} 
            navigate={null}
            isCollapsed={isResourcesOnLeaveCollapsed}
            onToggleCollapse={() => setIsResourcesOnLeaveCollapsed(!isResourcesOnLeaveCollapsed)}
            config={resourcesOnLeaveConfig}
          />
          {resourcesTotalTasks && (
            <div className="manager-stats-section resources-total-tasks-section">
              <div className="manager-stats-header">
                <div className="section-header-content">
                  <h2 className="text-lg font-semibold text-default-font">📊 Total Tasks This Week</h2>
                  <div className="section-summary">
                    <span className="total-tasks-badge">
                      <span className="badge-count-prominent">{resourcesTotalTasks.uniqueTaskCount || 0}</span>
                      <span className="badge-detail-text">unique tasks</span>
                    </span>
                    <span className="total-tasks-detail">
                      across {resourcesTotalTasks.workPlanCount || 0} work plans
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="manager-stats-wrapper">
          <OverdueCards 
            title="📅 Projects Active Yesterday"
            data={activeProjectsYesterday} 
            navigate={navigate}
            isCollapsed={isActiveProjectsYesterdayCollapsed}
            onToggleCollapse={() => setIsActiveProjectsYesterdayCollapsed(!isActiveProjectsYesterdayCollapsed)}
            config={activeProjectsYesterdayConfig}
          />
          <OverdueCards 
            title="📊 Project in Overdue"
            data={projectsWithOverdue} 
            navigate={navigate}
            isCollapsed={isManagerStatsCollapsed}
            onToggleCollapse={() => setIsManagerStatsCollapsed(!isManagerStatsCollapsed)}
            config={overdueStatsConfig}
          />
        </div>
        <div className="manager-stats-wrapper">
          <OverdueCards 
            title="🚨 Projects with No Active Tasks"
            data={projectsNoActiveTasks} 
            navigate={navigate}
            isCollapsed={isProjectsNoActiveTasksCollapsed}
            onToggleCollapse={() => setIsProjectsNoActiveTasksCollapsed(!isProjectsNoActiveTasksCollapsed)}
            config={projectsNoActiveTasksConfig}
          />
          <OverdueCards 
            title="⚠️ Projects Need More Tasks"
            data={projectsLowActiveTasks} 
            navigate={navigate}
            isCollapsed={isProjectsLowActiveTasksCollapsed}
            onToggleCollapse={() => setIsProjectsLowActiveTasksCollapsed(!isProjectsLowActiveTasksCollapsed)}
            config={projectsLowActiveTasksConfig}
          />
          <OverdueCards 
            title="⏰ Features Due in 5 Days"
            data={projectsWithDueSoon} 
            navigate={navigate}
            isCollapsed={isDueSoonStatsCollapsed}
            onToggleCollapse={() => setIsDueSoonStatsCollapsed(!isDueSoonStatsCollapsed)}
            config={dueSoonStatsConfig}
          />
        </div>
        <div className="manager-stats-wrapper">
          <OverdueCards 
            title="🔥 Projects Active (Last 7 Days)"
            data={activeProjects} 
            navigate={navigate}
            isCollapsed={isActiveProjectsCollapsed}
            onToggleCollapse={() => setIsActiveProjectsCollapsed(!isActiveProjectsCollapsed)}
            config={activeProjectsConfig}
          />
          <OverdueCards 
            title="✅ Healthy Projects"
            data={healthyProjects} 
            navigate={navigate}
            isCollapsed={isHealthyProjectsCollapsed}
            onToggleCollapse={() => setIsHealthyProjectsCollapsed(!isHealthyProjectsCollapsed)}
            config={healthyProjectsConfig}
          />
        </div>
        <div className="manager-stats-wrapper">
          <OverdueCards 
            title="👥 Resources with Low Tasks"
            data={resourcesLowTasks} 
            navigate={null}
            isCollapsed={isResourcesLowTasksCollapsed}
            onToggleCollapse={() => setIsResourcesLowTasksCollapsed(!isResourcesLowTasksCollapsed)}
            config={resourcesLowTasksConfig}
          />
          <OverdueCards 
            title="⏳ Resources on Same Task Long"
            data={resourcesSameTaskLong} 
            navigate={null}
            isCollapsed={isResourcesSameTaskLongCollapsed}
            onToggleCollapse={() => setIsResourcesSameTaskLongCollapsed(!isResourcesSameTaskLongCollapsed)}
            config={resourcesSameTaskLongConfig}
          />
          <OverdueCards 
            title="🔄 Tasks Worked &gt; 5 Times"
            data={tasksHighActivity} 
            navigate={null}
            isCollapsed={isTasksHighActivityCollapsed}
            onToggleCollapse={() => setIsTasksHighActivityCollapsed(!isTasksHighActivityCollapsed)}
            config={tasksHighActivityConfig}
          />
        </div>
      </>
    );
  };

  // If there are unclosed work plans, show message and prevent creating new ones
  if (unclosedWorkPlans.length > 0) {
    return (
      <>
        <VersionCheckBanner />
        <div className="dashboard">
          <div className="dashboard-container">
          <h1 className="text-2xl font-semibold text-default-font">Dashboard</h1>

          <AccessibleProjectsShortcut userRole={userRole} />

          {/* Today's Goals Section - Prominent at top */}
          {renderTodaysGoals()}

          {/* Manager Stats Section */}
          {renderManagerStats()}
          
          {/* Pinned Notes Section */}
          <PinnedNotesSection notes={pinnedNotes} navigate={navigate} user={user} />

          {/* Shared Notes Section */}
          <SharedNotesSection notes={sharedNotes} user={user} />

          <div className="unclosed-plans-message">
            <div className="alert alert-warning">
              <h2>Unclosed Work Plans Found</h2>
              <p>You have unclosed work plans from previous days. Please close them before creating a new work plan.</p>
              <div className="unclosed-plans-list">
                {unclosedWorkPlans.map(plan => (
                  <div key={plan.id} className="unclosed-plan-item">
                    <span>Work Plan from {new Date(plan.date).toLocaleDateString()}</span>
                    <Button onClick={() => handleCloseOldWorkPlan(plan.id)}>
                      Close Work Plan
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      </>
    );
  }

  return (
    <>
      <VersionCheckBanner />
      <div className="dashboard">
      <div className="dashboard-container">
        <h1 className="text-2xl font-semibold text-default-font">Dashboard</h1>

        <AccessibleProjectsShortcut userRole={userRole} />

        {/* Today's Goals Section - Prominent at top */}
        {renderTodaysGoals()}

        {/* Manager Stats Section */}
        {renderManagerStats()}

        {/* Pinned Notes Section */}
        <PinnedNotesSection notes={pinnedNotes} navigate={navigate} user={user} />

        {/* Shared Notes Section */}
        <SharedNotesSection notes={sharedNotes} user={user} />
        
        {!activeWorkPlan ? (
          <div className="no-plan-message">
            {closedTodayPlan ? (
              <WorkDayCompleteCard closedAt={closedTodayPlan.closed_at} />
            ) : (
              <>
                <p>You haven't started your work day yet.</p>
                {error ? (
                  <div className="error-message">{error}</div>
                ) : (
                  <Button onClick={handleCreateWorkPlan}>
                    Create Work Plan
                  </Button>
                )}
              </>
            )}
          </div>
        ) : (
          <>
            <div className="dashboard-actions">
              <Button onClick={handleCloseWorkDay}>Close Work Day</Button>
              {/* <Button 
                variant="secondary" 
                onClick={() => navigate('/work-plan-summary')}
              >
                View Summary
              </Button> */}
              {canModifyWorkPlan && (
                <Button 
                  variant="secondary" 
                  onClick={handleAddToWorkPlan}
                >
                  Add to Today's Work Plan
                </Button>
              )}
            </div>

            <div className="task-grid">
              {workPlanTasks.map(task => {
                return (
                <div key={task.id} className="task-card">
                  {/* Row 1: Type and Title, Status and View Icon */}
                  <div className="task-card-header">
                    <div className="flex items-center gap-2">
                      <TaskTypeIcon taskType={task.task_type} />
                      <h3>
                        {task.display_id && (
                          <span className="text-sm font-medium text-neutral-600 mr-2">
                            [{task.display_id}]
                          </span>
                        )}
                        {task.title}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={task.status_task.toLowerCase()}>{task.status_task}</Badge>
                      <button
                        className="task-view-details-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          openTaskSummaryModal(task);
                        }}
                        title="View task details"
                      >
                        <Info size={16} />
                      </button>
                    </div>
                  </div>

                  {/* Row 2: Project badges etc and stats badges */}
                  <div className="task-card-badges">
                    <div className="flex items-center gap-2 flex-wrap">
                      <ProjectBadge projectName={task.project?.name} />
                      {task.feature_tag && (
                        <span 
                          className="clickable-feature-tag"
                          onClick={(e) => handleFeatureTagClick(e, task.feature_tag)}
                          title="Click to view feature details"
                        >
                          <FeatureTag 
                            name={task.feature_tag.name} 
                            color={task.feature_tag.color} 
                            size="small" 
                          />
                        </span>
                      )}
                      <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} />
                    </div>
                  </div>

                  {/* Row 3: Description */}
                  <p className={`task-description ${!task.description ? 'task-description-empty' : ''}`}>
                    {task.description || 'No description available'}
                  </p>

                  {/* Row 4: Attachments with filenames and clickable links */}
                  {((task.attachments && task.attachments.length > 0) || (task.work_plan_attachments && task.work_plan_attachments.length > 0)) && (
                    <div className="task-attachments-list">
                      {task.attachments && task.attachments.length > 0 && (
                        <>
                          {task.attachments.map(attachment => (
                            <a
                              key={attachment.id}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="task-attachment-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PaperclipIcon size={14} />
                              <span>{attachment.file_name}</span>
                              {attachment.file_size && (
                                <span className="attachment-file-size">
                                  ({(attachment.file_size / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </a>
                          ))}
                        </>
                      )}
                      {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
                        <>
                          {task.work_plan_attachments.map(attachment => (
                            <a
                              key={attachment.id}
                              href={attachment.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="task-attachment-link"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <PaperclipIcon size={14} />
                              <span>{attachment.file_name}</span>
                              {attachment.file_size && (
                                <span className="attachment-file-size">
                                  ({(attachment.file_size / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </a>
                          ))}
                        </>
                      )}
                    </div>
                  )}

                  {taskGoals[task.id] && (
                    <div className="manager-goal-section">
                      <div 
                        className="manager-goal-header"
                        onClick={() => setExpandedGoalTaskId(expandedGoalTaskId === task.id ? null : task.id)}
                      >
                        <div className="manager-goal-indicator">
                          <FeatherTarget size={16} />
                          <span className="manager-goal-label">Manager Goal</span>
                          {taskGoals[task.id].status === 'achieved' ? (
                            <FeatherCheckCircle size={16} className="goal-status-icon goal-achieved" />
                          ) : taskGoals[task.id].status === 'not-achieved' ? (
                            <FeatherAlertCircle size={16} className="goal-status-icon goal-not-achieved" />
                          ) : null}
                        </div>
                        {expandedGoalTaskId === task.id ? (
                          <FeatherChevronUp size={16} />
                        ) : (
                          <FeatherChevronDown size={16} />
                        )}
                      </div>
                      {expandedGoalTaskId === task.id && (
                        <div className="manager-goal-content">
                          <div className="manager-goal-text">
                            <strong>Goal:</strong> Complete {taskGoals[task.id].displayId ? `[${taskGoals[task.id].displayId}]` : ''} {taskGoals[task.id].taskTitle || task.title}
                          </div>
                          <div className="manager-goal-status">
                            <strong>Status:</strong>{' '}
                            {taskGoals[task.id].status === 'achieved' ? (
                              <span className="goal-status-achieved">✓ Achieved</span>
                            ) : taskGoals[task.id].status === 'not-achieved' ? (
                              <span className="goal-status-not-achieved">Target for today</span>
                            ) : (
                              <span className="goal-status-pending">Target for today</span>
                            )}
                          </div>
                          {taskGoals[task.id].projectName && (
                            <div className="manager-goal-project">
                              <strong>Project:</strong> {taskGoals[task.id].projectName}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="task-details-section">
                    <h4>Task Details</h4>
                    {editingTaskDetailsId === task.id ? (
                      <div className="task-details-edit">
                        <TextArea label="Today's Work Description">
                          <TextArea.Input
                            value={workDescription}
                            onChange={(e) => handleWorkDescriptionChange(e.target.value)}
                            placeholder="Describe your work..."
                          />
                        </TextArea>
                        {taskDetailsValidationError && (
                          <div className="task-details-validation-error">
                            <FaExclamationTriangle /> {taskDetailsValidationError}
                          </div>
                        )}
                        <div className="task-details-row">
                          <TextField label="Hours">
                            <TextField.Input
                              type="number"
                              min="0"
                              step="0.5"
                              value={taskHours}
                              onChange={(e) => handleHoursChange(parseFloat(e.target.value) || 0)}
                              placeholder="0"
                            />
                          </TextField>
                          <div className="status-select-wrapper">
                            <label>Status</label>
                            <select
                              value={taskStatus}
                              onChange={(e) => handleStatusChange(e.target.value)}
                              className="status-select"
                            >
                              {Object.values(TASK_STATUS).map(status => (
                                <option key={status} value={status}>
                                  {status.charAt(0).toUpperCase() + status.slice(1).replace('-', ' ')}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="task-details-actions">
                          <Button variant="neutral" onClick={() => { setEditingTaskDetailsId(null); setTaskDetailsValidationError(null); }}>
                            Cancel
                          </Button>
                          <Button variant="primary" onClick={() => handleTaskDetailsSave(task.id)}>
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="task-details-view">
                        <div className="task-details-content">
                          <div className="task-details-field">
                            <span className="task-details-label">Today's Work Description:</span>
                            <p className={`task-notes ${!task.notes ? 'task-notes-empty' : ''}`}>
                              {task.notes || 'No work description yet'}
                            </p>
                          </div>
                          <div className="task-details-meta">
                            <div className="task-details-field">
                              <span className="task-details-label">Hours:</span>
                              <span className="task-hours-value">{task.hours || 0} hours</span>
                            </div>
                            <div className="task-details-field">
                              <span className="task-details-label">Status:</span>
                              <Badge variant={task.status_task.toLowerCase()}>{task.status_task}</Badge>
                            </div>
                          </div>
                        </div>
                        <Button 
                          variant="neutral" 
                          size="small" 
                          onClick={() => {
                            setEditingTaskDetailsId(task.id);
                            setTaskDetailsValidationError(null);
                            setWorkDescription(task.notes || '');
                            setTaskHours(task.hours || 0);
                            setTaskStatus(task.status_task || TASK_STATUS.OPEN);
                          }}
                        >
                          Edit Details
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Attachments Modal */}
                  {showAttachments === task.id && ((task.attachments && task.attachments.length > 0) || (task.work_plan_attachments && task.work_plan_attachments.length > 0)) && (
                    <div className="attachments-modal" onClick={() => setShowAttachments(null)}>
                      <div className="attachments-modal-content" onClick={e => e.stopPropagation()}>
                        <div className="attachments-modal-header">
                          <h3>Attachments</h3>
                          <button className="close-button" onClick={() => setShowAttachments(null)}>×</button>
                        </div>
                        <div className="attachments-list">
                          {task.attachments && task.attachments.length > 0 && (
                            <>
                              <h4 className="attachments-section-title">Task Attachments</h4>
                              {task.attachments.map(attachment => (
                                <div key={attachment.id} className="attachment-item">
                                  <a
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="attachment-link"
                                  >
                                    <FaPaperclip />
                                    <span>{attachment.file_name}</span>
                                    {attachment.file_size && (
                                      <span className="file-size">
                                        ({(attachment.file_size / 1024).toFixed(1)} KB)
                                      </span>
                                    )}
                                  </a>
                                </div>
                              ))}
                            </>
                          )}
                          {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
                            <>
                              {task.attachments && task.attachments.length > 0 && <h4 className="attachments-section-title">Work Plan Attachments</h4>}
                          {task.work_plan_attachments.map(attachment => (
                            <div key={attachment.id} className="attachment-item">
                              <a
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="attachment-link"
                              >
                                <FaPaperclip />
                                <span>{attachment.file_name}</span>
                                <span className="file-size">
                                  ({(attachment.file_size / 1024).toFixed(1)} KB)
                                </span>
                              </a>
                            </div>
                          ))}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          </>
        )}
      </div>

      {/* Feature Tag Detail Modal */}
      {selectedFeatureTag && (
        <FeatureTagDetailModal
          tag={selectedFeatureTag}
          isOpen={isFeatureTagModalOpen}
          onClose={() => {
            setIsFeatureTagModalOpen(false);
            setSelectedFeatureTag(null);
          }}
          tasks={workPlanTasks}
          users={uniqueUsers}
        />
      )}

      {/* Task Summary Modal */}
      <TaskSummaryModal
        task={selectedTaskForModal}
        isOpen={isTaskSummaryModalOpen}
        onClose={closeTaskSummaryModal}
        getTaskStatus={getTaskStatus}
        getStatusVariant={getStatusVariant}
        getTaskHistory={getTaskHistory}
        availableTags={[]}
        allTasks={allTasks}
        projectId={selectedTaskForModal?.project?.id}
        allowCrossProject={false}
        isManager={isManager(userRole)}
        onTaskClick={openTaskSummaryModal}
      />
    </div>
    </>
  );
};

export default Dashboard; 